import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Card, Button, message, Divider, Spin, Modal, Typography } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { useParams, useNavigate } from "react-router-dom";
import { CalculatorOutlined, EyeOutlined, ArrowLeftOutlined, ReloadOutlined } from "@ant-design/icons";
import {
    useCalculateAdminBrandEquityMutation,
    useGetStaleCachedAdminBrandEquityMutation,
    useGetAdminBrandsQuery,
    useGetCohortsByBrandQuery,
    useGetAdminSurveysQuery,
} from "../../services/nestjs-base-api";
import {
    ICalculateBrandEquityRequest,
    IBrandEquityScoreResponse,
    PeriodEnum,
} from "../../services/nestjs-base-api/types";
import BrandEquityFilters from "../../components/BrandEquity/BrandEquityFilters";
import OverallScoreCard from "../../components/BrandEquity/OverallScoreCard";
import QuestionCategoriesTable from "../../components/BrandEquity/QuestionCategoriesTable";
import LatentElementsBreakdown from "../../components/BrandEquity/LatentElementsBreakdown";
import BrandEquityVisualizationWrapper from "../../components/BrandEquity/BrandEquityVisualizationWrapper";

const { Title } = Typography;

export const AdminBrandEquityPage: React.FunctionComponent = () => {
    const { user } = useAuth();
    const { brandId } = useParams<{ brandId: string }>();
    const navigate = useNavigate();
    
    const [filters, setFilters] = useState<ICalculateBrandEquityRequest & {
        brandId?: number;
        periodType?: 'last30' | 'last60' | 'last90' | 'last120' | 'last180' | 'last365' | 'allTime' | 'custom';
        countryCodes?: string[];
    }>({});
    const [results, setResults] = useState<IBrandEquityScoreResponse | null>(null);
    const [calculating, setCalculating] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [visualizationModalVisible, setVisualizationModalVisible] = useState(false);
    const [visualizationModalReady, setVisualizationModalReady] = useState(false);
    const [visualizationType, setVisualizationType] = useState<'classic' | 'orbital' | 'orbital-v3' | 'cards' | null>(null);
    const [initialCalculationDone, setInitialCalculationDone] = useState(false);
    const [lastCalculatedAt, setLastCalculatedAt] = useState<string | null>(null);
    const [isStaleCache, setIsStaleCache] = useState(false);

    const [calculateAdminBrandEquity] = useCalculateAdminBrandEquityMutation();
    const [getStaleCachedAdminBrandEquity] = useGetStaleCachedAdminBrandEquityMutation();
    
    // Get brand info for the header
    const { data: brandsData } = useGetAdminBrandsQuery({
        jwtToken: user?.jwtToken || "",
        page: 1,
        limit: 1000, // Get all brands to find the specific one
    });
    
    const currentBrand = brandsData?.data?.find(b => b.id === parseInt(brandId || ''));
    
    // Load cohorts for the selected brand
    const { data: cohortsData } = useGetCohortsByBrandQuery(
        {
            jwtToken: user?.jwtToken || "",
            organisationId: currentBrand?.organisationId || 0,
            brandId: parseInt(brandId || '0')
        },
        { skip: !user?.jwtToken || !brandId || !currentBrand?.organisationId }
    );

    // Load surveys for country selection
    const { data: surveysResponse } = useGetAdminSurveysQuery(
        {
            jwtToken: user?.jwtToken || "",
            offset: 0,
            limit: 1000, // Get all surveys to find countries
            organisationId: currentBrand?.organisationId
        },
        { skip: !user?.jwtToken || !currentBrand?.organisationId }
    );

    // Filter surveys to only those for the current brand
    const brandSurveys = React.useMemo(() => {
        if (!surveysResponse?.data || !brandId) return [];
        return surveysResponse.data.filter(s => s.brandId === parseInt(brandId));
    }, [surveysResponse, brandId]);
    
    // Local storage key for admin brand-specific filters
    const FILTERS_STORAGE_KEY = `adminBrandEquityFilters_${brandId}_${user?.id || 'default'}`;

    // Load filters from localStorage and set defaults
    useEffect(() => {
        if (!brandId) return;
        
        // Skip if filters are already set
        if (filters.brandId) return;
        
        // Try to load from localStorage
        const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
        let loadedFilters: (ICalculateBrandEquityRequest & {
            brandId?: number;
            periodType?: 'last30' | 'last60' | 'last90' | 'last120' | 'last180' | 'last365' | 'allTime' | 'custom';
        }) | null = null;

        if (savedFilters) {
            try {
                const parsed = JSON.parse(savedFilters);

                // Migrate old filters: remove obsolete fields
                if (parsed.surveyIds) {
                    delete parsed.surveyIds;
                    parsed.cohortIds = undefined; // Migrate to "All Cohorts"
                }
                delete parsed.filterMode; // Remove obsolete field

                // For preset periods, DON'T set dates - let the API handle it
                if (parsed.periodType && parsed.periodType !== 'custom') {
                    loadedFilters = {
                        ...parsed,
                        // Remove any saved dates for preset periods
                        dateFrom: undefined,
                        dateTo: undefined,
                    };
                } else {
                    loadedFilters = parsed;
                }
            } catch (error) {
                console.error('Failed to parse saved filters:', error);
            }
        }

        // Set default filters - don't set dates for preset periods
        const defaultFilters = {
            brandId: parseInt(brandId),
            periodType: 'last120' as const,
            // Don't set dateFrom/dateTo for preset periods - let API handle it
        };

        const finalFilters = loadedFilters || defaultFilters;
        setFilters(finalFilters);
        
    }, [brandId, FILTERS_STORAGE_KEY, filters.brandId]);

    // Load stale cache on initial load instead of auto-calculating
    useEffect(() => {
        const loadStaleCache = async () => {
            if (!user?.jwtToken || !filters.brandId || results || initialCalculationDone) return;
            
            try {
                // Prepare filters similar to handleCalculate
                let updatedFilters = { ...filters };
                
                // Convert period selection to API format
                if (filters.periodType && filters.periodType !== 'custom') {
                    let period: PeriodEnum;
                    switch (filters.periodType) {
                        case 'last30':
                            period = PeriodEnum.THIRTY_DAYS;
                            break;
                        case 'last60':
                            period = PeriodEnum.SIXTY_DAYS;
                            break;
                        case 'last90':
                            period = PeriodEnum.NINETY_DAYS;
                            break;
                        case 'last120':
                            period = PeriodEnum.ONE_TWENTY_DAYS;
                            break;
                        case 'last180':
                            period = PeriodEnum.ONE_EIGHTY_DAYS;
                            break;
                        case 'last365':
                            period = PeriodEnum.THREE_SIXTY_FIVE_DAYS;
                            break;
                        case 'allTime':
                            period = PeriodEnum.ALL_TIME;
                            break;
                        default:
                            period = PeriodEnum.THIRTY_DAYS;
                    }
                    updatedFilters = {
                        ...updatedFilters,
                        period,
                        dateFrom: undefined,
                        dateTo: undefined,
                    };
                } else if (filters.periodType === 'custom') {
                    updatedFilters = {
                        ...updatedFilters,
                        period: PeriodEnum.DATE_RANGE,
                    };
                }

                const { brandId, periodType, ...apiPayload } = updatedFilters;
                
                const staleResult = await getStaleCachedAdminBrandEquity({
                    jwtToken: user.jwtToken,
                    brandId: parseInt(brandId as any),
                    payload: apiPayload,
                }).unwrap();
                
                if (staleResult) {
                    setResults(staleResult);
                    setLastCalculatedAt(staleResult.calculatedAt);
                    setIsStaleCache(staleResult.isStale || false);
                    setInitialCalculationDone(true);
                }
            } catch (error) {
                // Silently fail - user can click Calculate to get fresh results
                console.debug('No stale cache available', error);
            }
        };
        
        loadStaleCache();
    }, [user?.jwtToken, filters.brandId, getStaleCachedAdminBrandEquity, filters, initialCalculationDone]);

    // Save filters to localStorage whenever they change
    useEffect(() => {
        if (filters.brandId) {
            // For preset periods, don't save calculated dates - only save the period type
            const filtersToSave = { ...filters };
            if (filters.periodType && filters.periodType !== 'custom') {
                // Remove calculated dates for preset periods - they'll be recalculated fresh
                delete filtersToSave.dateFrom;
                delete filtersToSave.dateTo;
            }
            localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filtersToSave));
        }
    }, [filters, FILTERS_STORAGE_KEY]);

    const handleCalculate = useCallback(async () => {
        if (!user?.jwtToken || !filters.brandId) {
            message.error('Missing authentication or brand selection');
            return;
        }

        setCalculating(true);
        
        try {
            // Build payload based on filters
            const payload: ICalculateBrandEquityRequest = {
                // Always include brandIds for admin API
                brandIds: [filters.brandId]
            };

            // Add cohort filters if specified
            if (filters.cohortIds && filters.cohortIds.length > 0) {
                payload.cohortIds = filters.cohortIds;
            }

            // Add country filters if specified
            if (filters.countryCodes && filters.countryCodes.length > 0) {
                payload.countryCodes = filters.countryCodes;
            }

            // Handle period filtering
            if (filters.periodType) {
                // Period filter mode - use period enum for presets
                switch (filters.periodType) {
                    case 'last30':
                        payload.period = PeriodEnum.THIRTY_DAYS;
                        break;
                    case 'last60':
                        payload.period = PeriodEnum.SIXTY_DAYS;
                        break;
                    case 'last90':
                        payload.period = PeriodEnum.NINETY_DAYS;
                        break;
                    case 'last120':
                        payload.period = PeriodEnum.ONE_TWENTY_DAYS;
                        break;
                    case 'last180':
                        payload.period = PeriodEnum.ONE_EIGHTY_DAYS;
                        break;
                    case 'last365':
                        payload.period = PeriodEnum.THREE_SIXTY_FIVE_DAYS;
                        break;
                    case 'allTime':
                        payload.period = PeriodEnum.ALL_TIME;
                        break;
                    case 'custom':
                        if (filters.dateFrom && filters.dateTo) {
                            payload.period = PeriodEnum.DATE_RANGE;
                            payload.dateFrom = filters.dateFrom;
                            payload.dateTo = filters.dateTo;
                        } else {
                            payload.period = PeriodEnum.ONE_TWENTY_DAYS;
                        }
                        break;
                    default:
                        payload.period = PeriodEnum.ONE_TWENTY_DAYS;
                }
            } else if (filters.dateFrom && filters.dateTo) {
                // Direct date range
                payload.period = PeriodEnum.DATE_RANGE;
                payload.dateFrom = filters.dateFrom;
                payload.dateTo = filters.dateTo;
            } else {
                // Default to 120 days for admin
                payload.period = PeriodEnum.ONE_TWENTY_DAYS;
            }

            const result = await calculateAdminBrandEquity({
                jwtToken: user.jwtToken,
                brandId: filters.brandId,
                payload
            }).unwrap();

            setResults(result);
            setLastCalculatedAt(result.calculatedAt);
            setIsStaleCache(false); // Fresh results are not stale
            message.success('Brand equity calculation completed successfully');

        } catch (error: any) {
            console.error('Brand equity calculation failed:', error);
            message.error(error?.data?.message || 'Brand equity calculation failed');
        } finally {
            setCalculating(false);
        }
    }, [user?.jwtToken, filters, calculateAdminBrandEquity]);

    const handleFiltersChange = useCallback((newFilters: any) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            ...newFilters
        }));
    }, []);

    const handleRegenerate = useCallback(async () => {
        if (!user?.jwtToken || !filters.brandId) {
            message.error('Missing authentication or brand selection');
            return;
        }

        setRegenerating(true);
        
        try {
            // Build payload based on filters with force recalculate
            const payload: ICalculateBrandEquityRequest = {
                forceRecalculate: true, // This will bypass the cache
                // Always include brandIds for admin API
                brandIds: [filters.brandId]
            };

            // Add cohort filters if specified
            if (filters.cohortIds && filters.cohortIds.length > 0) {
                payload.cohortIds = filters.cohortIds;
            }

            // Add country filters if specified
            if (filters.countryCodes && filters.countryCodes.length > 0) {
                payload.countryCodes = filters.countryCodes;
            }

            // Handle period filtering
            if (filters.periodType) {
                // Period filter mode - use period enum for presets
                switch (filters.periodType) {
                    case 'last30':
                        payload.period = PeriodEnum.THIRTY_DAYS;
                        break;
                    case 'last60':
                        payload.period = PeriodEnum.SIXTY_DAYS;
                        break;
                    case 'last90':
                        payload.period = PeriodEnum.NINETY_DAYS;
                        break;
                    case 'last120':
                        payload.period = PeriodEnum.ONE_TWENTY_DAYS;
                        break;
                    case 'last180':
                        payload.period = PeriodEnum.ONE_EIGHTY_DAYS;
                        break;
                    case 'last365':
                        payload.period = PeriodEnum.THREE_SIXTY_FIVE_DAYS;
                        break;
                    case 'allTime':
                        payload.period = PeriodEnum.ALL_TIME;
                        break;
                    case 'custom':
                        if (filters.dateFrom && filters.dateTo) {
                            payload.period = PeriodEnum.DATE_RANGE;
                            payload.dateFrom = filters.dateFrom;
                            payload.dateTo = filters.dateTo;
                        } else {
                            payload.period = PeriodEnum.ONE_TWENTY_DAYS;
                        }
                        break;
                    default:
                        payload.period = PeriodEnum.ONE_TWENTY_DAYS;
                }
            } else if (filters.dateFrom && filters.dateTo) {
                // Direct date range
                payload.period = PeriodEnum.DATE_RANGE;
                payload.dateFrom = filters.dateFrom;
                payload.dateTo = filters.dateTo;
            } else {
                // Default to 120 days for admin
                payload.period = PeriodEnum.ONE_TWENTY_DAYS;
            }

            const result = await calculateAdminBrandEquity({
                jwtToken: user.jwtToken,
                brandId: filters.brandId,
                payload
            }).unwrap();

            setResults(result);
            setLastCalculatedAt(result.calculatedAt);
            setIsStaleCache(false); // Fresh results are not stale
            message.success('Brand equity regenerated successfully with fresh data');

        } catch (error: any) {
            console.error('Brand equity regeneration failed:', error);
            message.error(error?.data?.message || 'Brand equity regeneration failed');
        } finally {
            setRegenerating(false);
        }
    }, [user?.jwtToken, filters, calculateAdminBrandEquity]);

    if (!brandId) {
        return <div>Invalid brand ID</div>;
    }

    return (
        <div style={{ padding: '24px' }}>
            {/* Header */}
            <div style={{ marginBottom: 24 }}>
                <Button 
                    icon={<ArrowLeftOutlined />} 
                    onClick={() => navigate('/admin/brands')}
                    style={{ marginBottom: 16 }}
                >
                    Back to Brands
                </Button>
                <Title level={2} style={{ margin: 0 }}>
                    Brand Equity Analysis
                </Title>
                {currentBrand && (
                    <div style={{ marginTop: 8 }}>
                        <Title level={4} style={{ margin: 0, color: '#666' }}>
                            {currentBrand.name} ({currentBrand.organisationName})
                        </Title>
                    </div>
                )}
            </div>

            <Row gutter={[24, 24]}>
                <Col xs={24} lg={8}>
                    <Card title="Analysis Filters" style={{ height: 'fit-content' }}>
                        <BrandEquityFilters
                            value={filters}
                            onChange={handleFiltersChange}
                            cohorts={cohortsData || []}
                            brands={currentBrand ? [{
                                id: currentBrand.id,
                                name: currentBrand.name,
                                hash: currentBrand.hash,
                                logoUrl: '',
                                primaryColor: '#1890ff',
                                organisationId: currentBrand.organisationId,
                                createdAt: currentBrand.createdAt,
                                updatedAt: currentBrand.createdAt
                            }] : []}
                            surveys={brandSurveys as any[] || []}
                            onBrandChange={(brandId) => {
                                // Brand is fixed in admin view, do nothing
                            }}
                        />
                        
                        <Divider />
                        
                        {lastCalculatedAt && (
                            <div style={{ 
                                marginBottom: 16,
                                padding: '8px 12px',
                                background: isStaleCache ? '#fef3c7' : '#f3f4f6',
                                borderRadius: '4px',
                                fontSize: '13px',
                                color: isStaleCache ? '#92400e' : '#6b7280',
                                textAlign: 'center'
                            }}>
                                <div>Last calculated: {new Date(lastCalculatedAt).toLocaleString()}</div>
                                {isStaleCache && <div style={{ marginTop: '4px', fontStyle: 'italic' }}>Cached data - click Calculate or Regenerate for fresh results</div>}
                            </div>
                        )}
                        
                        <Button
                            type="primary"
                            icon={<CalculatorOutlined />}
                            onClick={handleCalculate}
                            loading={calculating || regenerating}
                            block
                            size="large"
                            style={{ marginBottom: 12 }}
                        >
                            Calculate Brand Equity
                        </Button>

                        <Button
                            icon={<ReloadOutlined />}
                            onClick={handleRegenerate}
                            loading={regenerating}
                            disabled={calculating}
                            block
                            style={{ marginBottom: 16 }}
                            title="Clear cache and force fresh calculation"
                        >
                            {regenerating ? 'Regenerating...' : 'Regenerate Brand Equity'}
                        </Button>

                        {results && (
                            <>
                                <Button
                                    icon={<EyeOutlined />}
                                    onClick={() => {
                                        setVisualizationType('classic');
                                        setVisualizationModalVisible(true);
                                    }}
                                    block
                                    style={{ marginBottom: 8 }}
                                >
                                    View Classic Visualization
                                </Button>
                                <Button
                                    icon={<EyeOutlined />}
                                    onClick={() => {
                                        setVisualizationType('orbital');
                                        setVisualizationModalVisible(true);
                                    }}
                                    block
                                    style={{ marginBottom: 8 }}
                                >
                                    View Orbital Visualization
                                </Button>
                                <Button
                                    icon={<EyeOutlined />}
                                    onClick={() => {
                                        setVisualizationType('orbital-v3');
                                        setVisualizationModalVisible(true);
                                    }}
                                    block
                                    style={{ marginBottom: 8 }}
                                >
                                    View Orbital V3 Visualization
                                </Button>
                                <Button
                                    icon={<EyeOutlined />}
                                    onClick={() => {
                                        setVisualizationType('cards');
                                        setVisualizationModalVisible(true);
                                    }}
                                    block
                                >
                                    View Card Visualization
                                </Button>
                            </>
                        )}
                    </Card>
                </Col>

                <Col xs={24} lg={16}>
                    {calculating && (
                        <Card style={{ textAlign: 'center', padding: '40px' }}>
                            <Spin size="large" />
                            <div style={{ marginTop: 16 }}>
                                <strong>Calculating Brand Equity...</strong>
                            </div>
                            <div style={{ marginTop: 8, color: '#666' }}>
                                This may take a few moments
                            </div>
                        </Card>
                    )}

                    {!calculating && results && (
                        <>
                            <OverallScoreCard results={results} />
                            <QuestionCategoriesTable categories={results.questionCategoryScores} />
                            <LatentElementsBreakdown elements={results.latentElementScores} />
                        </>
                    )}

                    {!calculating && !results && (
                        <Card style={{ textAlign: 'center', padding: '40px' }}>
                            <CalculatorOutlined style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
                            <div style={{ fontSize: 18, marginBottom: 8 }}>
                                <strong>Ready to Calculate Brand Equity</strong>
                            </div>
                            <div style={{ color: '#666' }}>
                                Configure your filters and click "Calculate Brand Equity" to begin analysis
                            </div>
                        </Card>
                    )}
                </Col>
            </Row>

            {/* Visualization Modal */}
            <Modal
                title={`Brand Equity Visualization - ${visualizationType === 'classic' ? 'Classic' : visualizationType === 'orbital' ? 'Orbital' : 'Cards'}`}
                open={visualizationModalVisible}
                onCancel={() => {
                    setVisualizationModalVisible(false);
                    setVisualizationModalReady(false);
                    setVisualizationType(null);
                }}
                afterOpenChange={(open) => {
                    if (open) {
                        setVisualizationModalReady(true);
                    }
                }}
                footer={null}
                width="100%"
                style={{ top: 0, maxWidth: '100%', margin: 0, padding: 0 }}
                styles={{
                    body: {
                        height: 'calc(100vh - 55px)',
                        overflow: visualizationType === 'cards' ? 'auto' : 'hidden',
                        padding: 0
                    }
                }}
            >
                {results && visualizationType && visualizationModalReady && (
                    <BrandEquityVisualizationWrapper
                        key={`${visualizationType}-${visualizationModalVisible}`}
                        results={results}
                        isModal={true}
                        isAdmin={true}
                        organisationId={currentBrand?.organisationId}
                        brandId={currentBrand?.id}
                        forceVisualizationType={visualizationType}
                    />
                )}
            </Modal>
        </div>
    );
};